import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm, useWatch, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep } from "@/components/ui/wizard/types";
import {
  PROTECTED_ROUTES,
  buildScheduleReturnRoute,
  resolveScheduleReturnStaffMemberId,
} from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import { useGymChangeHandler } from "@/hooks/use-gym-change-handler";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import {
  EventCreationSchema,
  type EventCreationFormData,
} from "@/schemas/event";
import {
  EventTypeStep,
  EventDetailsStep,
  ReviewStep,
} from "@/components/create-event/steps";
import { EVENTABLE_TYPE } from "@/constants/events";
import { RECURRENCE } from "@/utils/enum/dates";
import { eventQueryOptions, useUpdateEvent } from "@/queries/use-events";
import { formatInTimeZone } from "date-fns-tz";
import { isPast } from "date-fns";
import type { DateConstraints } from "@/types/date";
import { getScheduleDateConstraints } from "@/utils/gym-schedule-dates";
import { toast } from "@/components/ui/toast";
import { getApiErrorMessages } from "@/utils/api-error";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { RecurrenceEventsModal } from "@/components/event-detail/recurrence-events-modal";
import type { UpdateMode } from "@/api/events/types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useSelectedGym } from "@/hooks/use-selected-gym";
import { EventDetailSkeleton } from "@/components/event-detail/event-detail-skeleton";
import { RouterContext } from "@/types/router";
import { useBreadcrumbStore } from "@/stores/breadcrumb-store";

interface LoaderParams {
  params: { eventId: string };
  context: RouterContext;
}

async function loadEventData({ params, context }: LoaderParams) {
  const selectedGym = context.gymStore.getState().selectedGym;
  if (!selectedGym) return;

  const event = await context.queryClient.ensureQueryData(
    eventQueryOptions(selectedGym.id, Number(params.eventId)),
  );

  return {
    breadcrumb: event.title,
    hasActionButtons: false,
    event,
  };
}

export const Route = createFileRoute("/(protected)/allocation/$eventId/edit/")({
  component: RouteComponent,
  loader: loadEventData,
  pendingComponent: () => <EventDetailSkeleton />,
  validateSearch: z.object({
    staffMemberId: z.coerce.number().optional(),
  }),
});

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = Route.useParams();
  const { staffMemberId } = Route.useSearch();
  const { id: selectedGymId, timezone: gymTimezone } = useSelectedGym();

  const { data: event } = useSuspenseQuery(
    eventQueryOptions(selectedGymId, Number(eventId)),
  );

  const { mutate: updateEvent, isPending: isUpdating } = useUpdateEvent();
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);
  const [updateMode, setUpdateMode] = useState<UpdateMode | undefined>();
  const [pendingFormData, setPendingFormData] =
    useState<EventCreationFormData | null>(null);

  const defaultValues = useMemo((): EventCreationFormData => {
    const start = new Date(event.startsAt);
    const end = new Date(event.endsAt);
    return {
      id: event.id,
      gymId: event.gymId,
      title: event.title,
      eventableType: event.eventableType,
      difficultyLevel: event.difficultyLevel ?? undefined,
      disciplineId: event.disciplineId ?? undefined,
      roomId: event.roomId ?? undefined,
      recommendedCapacity: event.recommendedCapacity ?? undefined,
      roomCapacity: event.roomCapacity,
      staffMemberId: event.staffMemberId ?? undefined,
      description: event.description ?? "",
      day: formatInTimeZone(start, gymTimezone, "yyyy-MM-dd"),
      startTime: formatInTimeZone(start, gymTimezone, "HH:mm"),
      endTime: formatInTimeZone(end, gymTimezone, "HH:mm"),
      gymScheduleId: null,
      disciplineAbbreviation: event.disciplineAbbreviation ?? undefined,
      disciplineColor: event.disciplineColor ?? undefined,
      roomName: event.roomName ?? undefined,
      staffMemberName: event.staffMemberName ?? undefined,
      recurrenceRules: event.recurrenceRules
        ? {
            frequency: event.recurrenceRules.frequency,
            until: event.recurrenceRules.until,
          }
        : { frequency: RECURRENCE.Never, until: "" },
      reason: event.reason ?? undefined,
      bodyFatPercentage: event.bodyFatPercentage,
      nextEval: event.nextEval,
      isAnchorEvent: event.isAnchorEvent ?? false,
    };
  }, [event, gymTimezone]);

  const dateConstraints = useMemo((): DateConstraints | undefined => {
    if (!event.gymScheduleStartsAt || !event.gymScheduleEndsAt) return;
    return getScheduleDateConstraints(
      event.gymScheduleStartsAt,
      event.gymScheduleEndsAt,
      gymTimezone,
      { allowPast: isPast(new Date(event.startsAt)) },
    );
  }, [
    event.gymScheduleStartsAt,
    event.gymScheduleEndsAt,
    event.startsAt,
    gymTimezone,
  ]);

  const schema = EventCreationSchema();

  const form = useForm<EventCreationFormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues,
  });

  const title = useWatch({ control: form.control, name: "title" });
  const setBreadcrumbOverride = useBreadcrumbStore((s) => s.setOverride);
  useEffect(() => {
    setBreadcrumbOverride(title?.trim() ? title.trim() : null);
    return () => setBreadcrumbOverride(null);
  }, [title, setBreadcrumbOverride]);

  const step2Fields = useMemo((): FieldPath<EventCreationFormData>[] => {
    switch (event.eventableType) {
      case EVENTABLE_TYPE.FloorDuty:
      case EVENTABLE_TYPE.PersonalTrainingSession:
        return [
          "staffMemberId",
          "startTime",
          "endTime",
          "recurrenceRules.frequency",
          "recurrenceRules.until",
        ];
      case EVENTABLE_TYPE.PhysicalEvaluationSession:
        return [
          "staffMemberId",
          "startTime",
          "endTime",
          "recurrenceRules.frequency",
          "recurrenceRules.until",
          "bodyFatPercentage",
          "nextEval",
        ];
      case EVENTABLE_TYPE.Vacancy:
        return [
          "reason",
          "startTime",
          "endTime",
          "recurrenceRules.frequency",
          "recurrenceRules.until",
        ];
      case EVENTABLE_TYPE.GroupSession:
      default:
        return [
          "title",
          "startTime",
          "endTime",
          "difficultyLevel",
          "disciplineId",
          "roomId",
          "recommendedCapacity",
          "staffMemberId",
          "description",
          "recurrenceRules.frequency",
          "recurrenceRules.until",
          "isAnchorEvent",
        ];
    }
  }, [event.eventableType]);

  const steps: WizardStep<EventCreationFormData>[] = useMemo(
    () => [
      {
        id: "event-type",
        label: t("pages:calendar.wizard.steps.eventType"),
        component: (props) => (
          <EventTypeStep
            {...props}
            availableTypes={[event.eventableType]}
            fixedType={event.eventableType}
            dateConstraints={dateConstraints}
          />
        ),
        fields: ["eventableType", "day"],
      },
      {
        id: "event-details",
        label: t("pages:calendar.wizard.steps.eventDetails"),
        component: (props) => (
          <EventDetailsStep
            {...props}
            excludeEventId={event.id}
            maxDate={dateConstraints?.maxDate}
          />
        ),
        fields: step2Fields,
      },
      {
        id: "confirmation",
        label: t("pages:calendar.wizard.steps.confirmation"),
        component: (props) => <ReviewStep {...props} />,
      },
    ],
    [t, event.eventableType, event.id, step2Fields, dateConstraints],
  );

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
  });

  useGymChangeHandler({
    hasUnsavedChanges: form.formState.isDirty,
    handleCancelEdit,
    navigateOnChange: PROTECTED_ROUTES.CALENDAR,
  });

  const submitUpdate = (data: EventCreationFormData, mode?: UpdateMode) => {
    updateEvent(
      {
        gymId: String(selectedGymId),
        id: String(event.id),
        form: data,
        timezone: gymTimezone,
        updateMode: mode,
      },
      {
        onSuccess: () => {
          toast.success(t("pages:calendar.toasters.update.success.title"), {
            description: t(
              "pages:calendar.toasters.update.success.description",
            ),
          });
          // Cancel/leave (below) intentionally don't do this: they discard
          // the edit, so they return to the entry context, not the saved one.
          navigate(
            buildScheduleReturnRoute(
              resolveScheduleReturnStaffMemberId(
                staffMemberId,
                data.staffMemberId,
              ),
              data.day,
            ),
          );
        },
        onError: (error) => {
          toast.error(t("pages:calendar.toasters.update.error.title"), {
            description: getApiErrorMessages(error),
          });
        },
      },
    );
  };

  const handleComplete = (data: EventCreationFormData) => {
    if (event.recurrenceRules) {
      setPendingFormData(data);
      setRecurrenceModalOpen(true);
    } else {
      submitUpdate(data);
    }
  };

  const handleRecurrenceSave = () => {
    if (pendingFormData && updateMode) {
      setRecurrenceModalOpen(false);
      submitUpdate(pendingFormData, updateMode);
    }
  };

  const handleCancel = () => {
    handleCancelEdit(() => {
      navigate({
        to: PROTECTED_ROUTES.CALENDAR_DETAIL,
        params: { eventId },
        search: staffMemberId !== undefined ? { staffMemberId } : undefined,
      });
    });
  };

  const dirtyRecurrence = form.formState.dirtyFields.recurrenceRules;
  const recurrenceChange =
    typeof dirtyRecurrence === "object"
      ? !!(dirtyRecurrence?.frequency || dirtyRecurrence?.until)
      : !!dirtyRecurrence;

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:calendar.wizard.editTitle")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          isSubmitting={isUpdating}
          onCancel={handleCancel}
        />
      </PageContainer>

      <RecurrenceEventsModal
        open={recurrenceModalOpen}
        recurrenceChange={recurrenceChange}
        updateMode={updateMode}
        setUpdateMode={setUpdateMode}
        onClose={() => {
          setRecurrenceModalOpen(false);
          setUpdateMode(undefined);
          setPendingFormData(null);
        }}
        onSave={handleRecurrenceSave}
      />

      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
